import {Button} from '@material-ui/core';
import {AsyncPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {withValueChanged} from 'Shared/utilities/withValueChanged';
import {
  changePreregisteredAgentFilter,
  deletePreregistration,
  getPreregisteredAgents,
  resendEmail,
} from 'Users/ducks/independentAgent';
import {PreregisteredAgent, PreregisteredAgentFilter} from 'Users/models/PreregisteredAgent';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {hasAccessToScopeByApplication} from 'Shared/utilities/applicationId';
import {Application} from 'Users/models/Application';

type PendingAgentRegistrationProps = {
  tabHeader: JSX.Element;
  registrations: AsyncPage<Page<PreregisteredAgent>>;
  getPreregisteredAgents: typeof getPreregisteredAgents;
  resendEmail: typeof resendEmail;
  identifier: string;
  filter: PreregisteredAgentFilter;
  deletePreregistration: typeof deletePreregistration;
  changePreregisteredAgentFilter: typeof changePreregisteredAgentFilter;
};

export const PendingAgentRegistration = (props: PendingAgentRegistrationProps) => {
  const scopes = useSelector(selectUserScopes);
  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const encryptedIdentifier = O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined;
  useEffect(() => {
    props.getPreregisteredAgents(props.filter, undefined, encryptedIdentifier);
  }, [props.filter]);
  
  const changeFilter = withValueChanged(props.filter, props.changePreregisteredAgentFilter);
  const filterProperties = {
    changeFilter,
    filterData: props.filter,
  };

  return (
    <PagedView
      pagedViewKey="pendingAgentRegistration"
      loadMore={page => props.getPreregisteredAgents(props.filter, page, encryptedIdentifier)}
      data={props.registrations}
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="First Name"
            filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
            default
            ddprivacy="mask"
          >
            {registration => registration.firstName}
          </Column>
          <Column
            {...columnProps}
            title="Last Name"
            filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
            default
          >
            {registration => registration.lastName}
          </Column>
          <Column
            {...columnProps}
            title="Email"
            filterProperties={withStringFilterProperties(filterProperties, 'email')}
            default
            ddprivacy="mask"
          >
            {registration => registration.email}
          </Column>
          <Column {...columnProps} isActions title="" default>
            {registration => (
              <>
                <RequireScope scopes={scopes} scope={Scope.SendRegistrationEmail}>
                  <Button
                    id={'pending-agent-registration-resend-email-button__' + registration.id}
                    color="primary"
                    onClick={() => {
                      props.resendEmail(registration.id, encryptedIdentifier);
                    }}
                  >
                    Resend Email
                  </Button>
                </RequireScope>
                {hasAccessToScopeByApplication(scopes, Scope.DeletePreregistrationUser, Application.Agent) && (
                  <Button
                    id={'pending-agent-registration-delete-registration-button__' + registration.id}
                    color="primary"
                    onClick={() => {
                      props.deletePreregistration(registration.id, props.filter, encryptedIdentifier);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default PendingAgentRegistration;
